import React from 'react';
import { cn } from '@/lib/utils';

export interface CinematicSectionProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  withBackground?: boolean;
  backgroundImage?: string;
  overlay?: 'gradient-right' | 'gradient-bottom' | 'gradient-top' | 'solid' | 'none';
  overlayOpacity?: number;
  minHeight?: string;
  container?: 'default' | 'small' | 'full';
  spacing?: 'default' | 'small' | 'large';
}

export const CinematicSection = React.forwardRef<HTMLElement, CinematicSectionProps>(
  ({ 
    className,
    children,
    withBackground = false,
    backgroundImage,
    overlay = 'none',
    overlayOpacity = 0.6,
    minHeight,
    container = 'default',
    spacing = 'default',
    ...props 
  }, ref) => {
    const containerStyles = {
      default: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
      small: 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8',
      full: 'w-full px-4 sm:px-6 lg:px-8',
    };

    const spacingStyles = {
      default: 'py-20 md:py-24',
      small: 'py-12 md:py-16',
      large: 'py-24 md:py-32',
    };

    const overlayStyles = {
      'gradient-right': `bg-gradient-to-r from-background via-background/${Math.round(overlayOpacity * 100)} to-transparent`,
      'gradient-bottom': `bg-gradient-to-t from-background via-background/${Math.round(overlayOpacity * 100)} to-transparent`,
      'gradient-top': `bg-gradient-to-b from-background via-background/${Math.round(overlayOpacity * 100)} to-transparent`,
      'solid': `bg-background/${Math.round(overlayOpacity * 100)}`,
      'none': '',
    };

    return (
      <section
        ref={ref}
        className={cn(
          'relative overflow-hidden',
          spacingStyles[spacing],
          className
        )}
        style={minHeight ? { minHeight } : undefined}
        {...props}
      >
        {/* Background Image */}
        {withBackground && backgroundImage && (
          <div className="absolute inset-0 z-0">
            <img
              src={backgroundImage}
              alt=""
              className="w-full h-full object-cover"
            />
            {overlay !== 'none' && (
              <div className={cn('absolute inset-0', overlayStyles[overlay])} />
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn('relative z-10', containerStyles[container])}>
          {children}
        </div>
      </section>
    );
  }
);

CinematicSection.displayName = 'CinematicSection';

export default CinematicSection;
